import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "api/landlord/applications";
import { VerificationNames, VerificationStatus } from "shared/types";
import { VerificationReport } from "types/landlord/reports";
import { getVerificationQueryKeys } from "views/applications-id-report/utils/getVerificationQueryKeys";
import { CombinedIncomeReport } from "../../../types";
import { getReverifyRequests } from "../../../slices/ReverifySlice/utils";
import { getFilteredBankStatements, filterIncomes } from "../utils";

interface Props {
  applicationId?: number;
  applicantId?: number;
  currentlySelectedVerificationId?: number;
}

export const useReportDataController = ({
  applicationId,
  applicantId,
  currentlySelectedVerificationId
}: Props) => {
  const {
    data: verificationData,
    isError,
    isLoading,
    refetch
  } = useQuery<VerificationReport<CombinedIncomeReport>>({
    queryKey: getVerificationQueryKeys({
      reportName: VerificationNames.CombinedIncome,
      applicationId: Number(applicationId || 0),
      applicantId: Number(applicantId || 0),
      verificationId: currentlySelectedVerificationId
    }),
    queryFn: () =>
      api
        .getApplicantVerification(
          Number(applicationId || 0),
          Number(applicantId || 0),
          currentlySelectedVerificationId || 0
        )
        .then((data) => data.verification)
  });

  const composedReportData = useMemo(() => {
    const verificationReportData = verificationData?.report;
    const settings = verificationData?.settings;
    const verificationStatus =
      verificationData?.status ?? VerificationStatus.Pending;
    const {
      pendingReverifyRequests,
      completedReverifyRequests,
      failedReverifyRequests
    } = getReverifyRequests(verificationData);

    return {
      verificationReportData,
      settings,
      verificationStatus,
      filteredBankStatements: getFilteredBankStatements(verificationData),
      pendingReverifyRequests,
      completedReverifyRequests,
      failedReverifyRequests,
      incomes: verificationReportData
        ? filterIncomes(verificationReportData.incomes, verificationStatus)
        : [],
      incomeSourceProvidersMap: Object.fromEntries(
        (verificationData?.income_sources ?? []).map((s) => [
          s.id,
          s.provider ?? ""
        ])
      ) as Record<number, string>,
      incomeSourceTrustScoresMap: Object.fromEntries(
        (verificationReportData?.incomes ?? [])
          .flatMap((income) => income.sources ?? [])
          .filter((s) => s.trust_score !== undefined)
          .map((s) => [s.id, s.trust_score])
      ) as Record<number, number>
    };
  }, [verificationData]);

  return {
    verificationData,
    ...composedReportData,
    isLoading,
    isError,
    refetch
  };
};
